/**
 * Application constants - All constants defined in one place
 */

// Screen definitions
export const SCREENS = {
  WELCOME: 'welcome',
  LOGIN: 'login',
  SIGNUP: 'signup',
  SIGNUP_PHONE: 'signupPhone', // Fixed: Using a consistent identifier
  VOICE_SIGNUP: 'voiceSignup',
  ONBOARDING: 'onboarding', // New: Onboarding flow
  VERIFY_CODE: 'verifyCode',
  VERIFY_EMAIL_CODE: 'verifyEmailCode', // Added: Email verification screen
  VERIFY_PHONE_CODE: 'verifyPhoneCode', // Added: Phone verification screen
  ACCOUNT_INFO: 'accountInfo',
  INTEREST_SELECTION: 'interestSelection',
  ACCOUNT_CREATED_SUCCESS: 'accountCreatedSuccess',
  FORGOT_PASSWORD: 'forgotPassword',
  NEW_PASSWORD: 'newPassword',
  DASHBOARD: 'dashboard',
  DIGITAL_VAULT: 'digitalVault',
  PROFILE: 'profile',
  QR_SCANNER: 'qrScanner',
  MESSAGING: 'messaging',
  IDENTITY_CIRCLE: 'identityCircle',
  NOTIFICATIONS: 'notifications',
  TODAY: 'today',
  SETTINGS: 'settings',
  DIGITAL_DISCIPLINE: 'digitalDiscipline', // Added: Digital Discipline screen
  UNIFIED_STATISTICS: 'unifiedStatistics', // Added: New unified statistics screen
  STATISTICS: 'istatistikler', // Added: Statistics screen constant
  ISTATISTIKLER: 'istatistikler', // Added: Turkish statistics screen constant

  // Güvenlik ekranları
  TWO_FACTOR_AUTH: 'twoFactorAuth', // 2FA screen constant
  ENDLESS_NOTES: 'endlessNotes', // Endless Notes screen constant
  LANGUAGE_AND_REGION: 'languageAndRegion', // Language and region settings screen
  QR_STATISTICS_SCREEN: 'QRStatisticsScreen', // QR Statistics screen constant

  // Content Creation Screens
  TEXT_CREATION: 'textCreation',
  VOICE_CREATION: 'voiceCreation',
  MEDIA_CREATION: 'mediaCreation',
  IMAGE_CREATION: 'imageCreation',
  VIDEO_CREATION: 'videoCreation'
};

// Country codes
export const COUNTRY_CODES = [
  { code: '90', country: 'TR', flag: '🇹🇷', name: 'Türkiye', dialCode: '90', region: 'Europe' },
  { code: '1', country: 'US', flag: '🇺🇸', name: 'United States', dialCode: '1', region: 'North America' },
  { code: '44', country: 'GB', flag: '🇬🇧', name: 'United Kingdom', dialCode: '44', region: 'Europe' },
  { code: '49', country: 'DE', flag: '🇩🇪', name: 'Germany', dialCode: '49', region: 'Europe' },
  { code: '33', country: 'FR', flag: '🇫🇷', name: 'France', dialCode: '33', region: 'Europe' },
  { code: '39', country: 'IT', flag: '🇮🇹', name: 'Italy', dialCode: '39', region: 'Europe' },
  { code: '34', country: 'ES', flag: '🇪🇸', name: 'Spain', dialCode: '34', region: 'Europe' },
  { code: '7', country: 'RU', flag: '🇷🇺', name: 'Russia', dialCode: '7', region: 'Eurasia' },
  { code: '86', country: 'CN', flag: '🇨🇳', name: 'China', dialCode: '86', region: 'Asia' },
  { code: '81', country: 'JP', flag: '🇯🇵', name: 'Japan', dialCode: '81', region: 'Asia' },
  { code: '91', country: 'IN', flag: '🇮🇳', name: 'India', dialCode: '91', region: 'Asia' },
  { code: '55', country: 'BR', flag: '🇧🇷', name: 'Brazil', dialCode: '55', region: 'South America' },
  { code: '52', country: 'MX', flag: '🇲🇽', name: 'Mexico', dialCode: '52', region: 'North America' },
  { code: '971', country: 'AE', flag: '🇦🇪', name: 'United Arab Emirates', dialCode: '971', region: 'Middle East' },
  { code: '966', country: 'SA', flag: '🇸🇦', name: 'Saudi Arabia', dialCode: '966', region: 'Middle East' },
  { code: '20', country: 'EG', flag: '🇪🇬', name: 'Egypt', dialCode: '20', region: 'Africa' },
  { code: '27', country: 'ZA', flag: '🇿🇦', name: 'South Africa', dialCode: '27', region: 'Africa' },
  { code: '61', country: 'AU', flag: '🇦🇺', name: 'Australia', dialCode: '61', region: 'Oceania' },
  { code: '64', country: 'NZ', flag: '🇳🇿', name: 'New Zealand', dialCode: '64', region: 'Oceania' },
  { code: '82', country: 'KR', flag: '🇰🇷', name: 'South Korea', dialCode: '82', region: 'Asia' }
];

// Speech recognition constants
export const VOICE_RECOGNITION = {
  LANGUAGES: [
    { code: 'tr-TR', name: 'Türkçe' },
    { code: 'en-US', name: 'English' },
    { code: 'de-DE', name: 'Deutsch' },
    { code: 'fr-FR', name: 'Français' },
    { code: 'es-ES', name: 'Español' }
  ],
  TIMEOUT: 10000, // 10 seconds timeout for voice recognition
  MAX_ATTEMPTS: 3,
  CONFIDENCE_THRESHOLD: 0.7
};

// Interest areas
export const INTEREST_OPTIONS = [
  { category: 'Teknoloji', tags: ['Kripto borsası', 'Blockchain', 'Telefonlar', 'Oyun', 'Yapay Zeka', 'Web3'] },
  { category: 'Tasarım', tags: ['Sanat', 'Çizim', 'Estetik', 'Grafik tasarım', 'Moda', 'Dijital Sanat', 'NFT'] },
  { category: 'Moda', tags: ['Giyim', 'Makyaj', 'Ünlülerin hayatı', 'Ayakkabılar', 'Sürdürülebilir Moda'] },
  { category: 'Hayvanlar', tags: ['Evcil hayvanlar', 'Yemek tarifleri', 'Kediler', 'Köpekler', 'Doğa Koruma'] },
  { category: 'İş hayatı', tags: ['Ekip çalışması', 'Ofis ortamı', 'Evden çalışma', 'Ofis', 'Girişimcilik', 'Yatırım'] },
  { category: 'Dil', tags: ['Psikoloji', 'İlişkiler', 'Psikanaliz', 'Kitaplar', 'Bilim dergileri', 'Felsefe'] },
  { category: 'Doğa', tags: ['Dağcılık', 'Su sporları', 'Gök gözlemciliği', 'Uzay', 'Çevre Bilinci', 'İklim Değişikliği'] }
];

// Digital vault tabs
export const VAULT_TABS = [
  { id: 'digitalAssets', label: 'Dijital Varlıklar' },
  { id: 'notes', label: 'Notlar' },
  { id: 'files', label: 'Dosyalar' }
];

// Endless Notes constants
export const ENDLESS_NOTES = {
  // Node types
  NODE_TYPES: {
    thought: { color: '#6366F1', width: 180, height: 90 },
    task: { color: '#10B981', width: 180, height: 90 },
    question: { color: '#F59E0B', width: 180, height: 90 },
    insight: { color: '#EC4899', width: 180, height: 90 },
    quote: { color: '#8B5CF6', width: 180, height: 90 },
    image: { color: '#0EA5E9', width: 200, height: 160 },
    audio: { color: '#EF4444', width: 200, height: 120 },
    video: { color: '#8B5CF6', width: 240, height: 180 },
  },
  // Connection types
  CONNECTION_TYPES: [
    { id: 'relates', label: 'İlişkili', color: '#6B7280' },
    { id: 'supports', label: 'Destekler', color: '#10B981' },
    { id: 'opposes', label: 'Karşıtı', color: '#EF4444' },
    { id: 'causes', label: 'Neden', color: '#F59E0B' },
    { id: 'example', label: 'Örnek', color: '#8B5CF6' },
  ],
  // Default node contents
  DEFAULT_NODES: {
    welcome: {
      id: 'welcome',
      type: 'insight',
      content: 'Endless Notes\'a Hoş Geldiniz',
      position: { offsetX: -150, offsetY: -100 },
      color: '#EC4899',
    },
    explanation: {
      id: 'explanation',
      type: 'thought',
      content: 'Bu merkeziyetsiz zihin sarayınızdır. Düşüncelerinizi düzenleyebilir ve blockchain\'de saklayabilirsiniz.',
      position: { offsetX: -100, offsetY: 50 },
      color: '#6366F1',
    },
    tip: {
      id: 'tip',
      type: 'question',
      content: 'İpucu: Notları sürükleyebilir, bağlantılar kurabilir ve farklı not tipleri oluşturabilirsiniz.',
      position: { offsetX: 150, offsetY: -50 },
      color: '#F59E0B',
    }
  },
  // Canvas actions
  ACTIONS: {
    MOVE: 'move',
    CONNECT: 'connect',
    DRAW: 'draw',
    ERASE: 'erase'
  },
  // Storage keys
  STORAGE_KEYS: {
    CANVAS_DATA: 'endless_notes_data',
    JOURNAL_ENTRIES: 'endless_notes_journal'
  }
};

// Sample transactions
export const SAMPLE_TRANSACTIONS = [
  { type: '+IDCOIN', amount: '0.03', merchant: 'TRENDYOL', date: '10/02/2023' },
  { type: '+IDCOIN', amount: '0.03', merchant: 'TRENDYOL', date: '09/02/2023' },
  { type: '-BTC', amount: '0.0000003', merchant: 'BURGER KING', date: '08/02/2023' },
  { type: '-ETH', amount: '0.0049', merchant: 'SPOTIFY', date: '07/02/2023' }
];

// Sample notes
export const SAMPLE_NOTES = [
  "Kediye mama ver.",
  "Marketten tavuk alınacak.",
  "Akşam eve geldiğinde kıyafetleri ütüle.",
  "Yarın gelecek e-postaları kontrol et.",
  "Kardeşini otogardan almaya git."
];

// File categories
export const FILE_CATEGORIES = [
  { icon: '📸', name: 'Fotoğraflar' },
  { icon: '📄', name: 'PDF Dosyaları' },
  { icon: '🔊', name: 'Ses Dosyaları' },
  { icon: '📱', name: 'Ekran Görüntüleri' }
];

// Navigation options - With translation keys
export const NAVIGATION_ITEMS = [
  { id: 'gameHub', icon: 'Wallet', label: 'GameHub', screen: 'gameHub' },
  { id: 'messages', icon: 'Mail', label: 'Mesajlar', screen: 'messaging' },
  { id: 'qr', icon: 'QrCode', label: 'QR', screen: 'qrScanner' },
  { id: 'today', icon: 'Calendar', label: 'Bugün', screen: 'today' },
  { id: 'profile', icon: 'User', label: 'Profil', screen: 'profile' }
  // Statistics item removed as requested
];

// Security levels
export const SECURITY_LEVELS = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  MAXIMUM: 'maximum'
};

// App colors
export const APP_COLORS = {
  PRIMARY: {
    50: '#eef2ff',
    100: '#e0e7ff',
    200: '#c7d2fe',
    300: '#a5b4fc',
    400: '#818cf8',
    500: '#6366f1',
    600: '#4f46e5',
    700: '#4338ca',
    800: '#3730a3',
    900: '#312e81'
  },
  SECONDARY: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a'
  },
  SUCCESS: {
    500: '#10b981',
    600: '#059669'
  },
  WARNING: {
    500: '#f59e0b',
    600: '#d97706'
  },
  ERROR: {
    500: '#ef4444',
    600: '#dc2626'
  }
};

// Animation constants
export const ANIMATION = {
  TRANSITION_DURATION: 300, // ms
  PULL_THRESHOLD: 80, // px
};

// Local storage keys
export const STORAGE_KEYS = {
  USER: 'idapps_user',
  AUTH_TOKEN: 'idapps_auth_token',
  THEME: 'idapps_theme',
  LOCALE: 'idapps_locale',
  BIOMETRIC_ENABLED: 'idapps_biometric_enabled',
  SECURITY_LEVEL: 'idapps_security_level',
  LAST_ACTIVE: 'idapps_last_active',
  TWO_FA_DATA: 'idapps_2fa_data',
  TWO_FA_ENABLED: 'idapps_2fa_enabled', // 2FA status key
  CUSTOM_LINKS: 'idapps_custom_links',
  TWO_FA_BACKUP_CODES: 'idapps_2fa_backup_codes',
  TWO_FA_RECOVERY_KEY: 'idapps_2fa_recovery_key',
  TWO_FA_VERIFIED_DEVICES: 'idapps_2fa_verified_devices',
  ENDLESS_NOTES_DATA: 'idapps_endless_notes_data', // Endless Notes data
  ENDLESS_NOTES_JOURNAL: 'idapps_endless_notes_journal', // Endless Notes journal entries
  ENDLESS_NOTES_BLOCKCHAIN: 'idapps_endless_notes_blockchain', // Blockchain record info
  VOICE_PREFERENCES: 'idapps_voice_preferences' // Voice recognition preferences
};

// App settings
export const APP_SETTINGS = {
  DEFAULT_LANGUAGE: 'tr',
  DEFAULT_THEME: 'light',
  DEFAULT_SECURITY_LEVEL: SECURITY_LEVELS.HIGH,
  INACTIVITY_TIMEOUT: 5 * 60 * 1000, // 5 minutes (milliseconds)
  CRYPTO_REFRESH_INTERVAL: 60 * 1000, // 1 minute (milliseconds)
  TOKEN_EXPIRATION: 24 * 60 * 60 * 1000, // 24 hours (milliseconds)
  BIOMETRIC_TIMEOUT: 15 * 60 * 1000, // 15 minutes (milliseconds)
  ENDLESS_NOTES_AUTOSAVE: 30 * 1000, // 30 seconds (auto-save)
  VOICE_RECOGNITION_TIMEOUT: 30 * 1000 // 30 seconds voice recognition timeout
};

// Two-factor authentication settings
export const TWO_FA_SETTINGS = {
  BACKUP_CODES_COUNT: 10,   // Number of backup codes to generate
  CODE_LENGTH: 6,           // TOTP code length
  TIME_STEP: 30,            // TOTP time step in seconds
  MAX_DEVICES: 5,           // Maximum number of verified devices
  RECOVERY_KEY_LENGTH: 24,  // Recovery key length
  MERKLE_TREE_DEPTH: 10     // Merkle tree depth
};